import React, { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Play, Pause, X, Volume2, RotateCcw, RotateCw, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { AnimatedIcon } from '../components/AnimatedIcon';
import i18nInstance from '../i18n';

interface Props {
  url: string;
  title: string;
  bookTitle: string;
  onClose: () => void;
  /** Posição inicial em segundos (retomar de onde parou) */
  startAt?: number;
  /** Chamado durante a reprodução, ao pausar (force) e ao fechar (force) */
  onProgress?: (position: number, duration: number, force?: boolean) => void;
  onEnded?: (duration: number) => void;
}

const AudiobookPlayer: React.FC<Props> = ({ url, title, bookTitle, onClose, startAt = 0, onProgress, onEnded }) => {
  const { t } = useTranslation();

  const audioRef = useRef<HTMLAudioElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const startAtRef = useRef(startAt);
  startAtRef.current = startAt;

  const [volume, setVolume] = useState(() => {
    const saved = localStorage.getItem('libritui_audio_volume');
    return saved ? parseFloat(saved) : 1.0;
  });
  const [showVolume, setShowVolume] = useState(false);

  useEffect(() => {
    setIsLoading(true);
    if (audioRef.current) {
      audioRef.current.load();
      audioRef.current.volume = volume;
      audioRef.current.playbackRate = playbackRate;
      audioRef.current.play().catch(console.error);
    }
  }, [url]);

  // Click outside to close volume slider
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setShowVolume(false);
      }
    };
    if (showVolume) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showVolume]);

  const togglePlay = () => {
    if (audioRef.current?.paused) audioRef.current.play();
    else audioRef.current?.pause();
    setIsPlaying(!isPlaying);
  };

  const formatTime = (time: number) => {
    if (!time || isNaN(time)) return '0:00';
    const mins = Math.floor(time / 60);
    const secs = Math.floor(time % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime);
      setProgress((audioRef.current.currentTime / audioRef.current.duration) * 100);
      onProgress?.(audioRef.current.currentTime, audioRef.current.duration || 0);
    }
  };

  const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = parseFloat(e.target.value);
    if (audioRef.current) {
      audioRef.current.currentTime = (val / 100) * audioRef.current.duration;
      setProgress(val);
    }
  };

  const skip = (seconds: number) => {
    if (audioRef.current) audioRef.current.currentTime += seconds;
  };

  const cycleRate = () => {
    const rates = [1, 1.25, 1.5, 0.75];
    const nextRate = rates[(rates.indexOf(playbackRate) + 1) % rates.length];
    setPlaybackRate(nextRate);
    if (audioRef.current) audioRef.current.playbackRate = nextRate;
  };

  const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = parseFloat(e.target.value);
    setVolume(v);
    if (audioRef.current) {
      audioRef.current.volume = v;
    }
    localStorage.setItem('libritui_audio_volume', v.toString());
  };

  const handleClose = () => {
    if (audioRef.current) onProgress?.(audioRef.current.currentTime, audioRef.current.duration || 0, true);
    onClose();
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
      className="fixed bottom-24 md:bottom-8 inset-x-4 mx-auto max-w-xl z-[200]"
    >
      <div className="bg-surface/90 backdrop-blur-2xl p-5 rounded-[28px] border border-black/[0.06] dark:border-white/[0.08] shadow-[0_24px_60px_-24px_rgba(0,0,0,0.35)] flex flex-col gap-4">

        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            {/* ondas de som enquanto toca */}
            <span className="w-10 h-10 shrink-0 rounded-full bg-tint-lavender flex items-end justify-center gap-[3px] pb-3">
              {[0, 1, 2, 3].map(i => (
                <motion.span
                  key={i}
                  className="w-[3px] rounded-full bg-ink/70"
                  animate={isPlaying ? { height: [5, 14, 7, 12, 5] } : { height: 5 }}
                  transition={{ duration: 1.1, repeat: isPlaying ? Infinity : 0, delay: i * 0.12, ease: 'easeInOut' }}
                />
              ))}
            </span>
            <div className="min-w-0">
              <p className="text-[10px] uppercase font-mono tracking-[0.14em] text-muted truncate">{bookTitle}</p>
              <h4 className="text-sm text-ink truncate">{title}</h4>
            </div>
          </div>
          <button onClick={handleClose} className="w-9 h-9 shrink-0 rounded-full bg-ink/[0.05] hover:bg-ink/[0.1] text-ink flex items-center justify-center transition-colors" aria-label="Fechar">
            <AnimatedIcon motion="spin"><X size={16} strokeWidth={1.75} /></AnimatedIcon>
          </button>
        </div>

        <audio
          ref={audioRef}
          src={url}
          onTimeUpdate={handleTimeUpdate}
          onLoadedMetadata={() => {
            const a = audioRef.current;
            setDuration(a?.duration || 0);
            setIsLoading(false);
            // retomar de onde parou (se não estiver no fim)
            if (a && startAtRef.current > 2 && startAtRef.current < (a.duration || Infinity) - 3) a.currentTime = startAtRef.current;
          }}
          onPlay={() => setIsPlaying(true)}
          onPause={() => { setIsPlaying(false); if (audioRef.current) onProgress?.(audioRef.current.currentTime, audioRef.current.duration || 0, true); }}
          onEnded={() => onEnded?.(audioRef.current?.duration || 0)}
          onError={() => setIsLoading(false)}
        />

        <div className="space-y-1.5">
          <div className="relative h-1.5 rounded-full bg-ink/[0.07]">
            <div className="absolute inset-y-0 left-0 rounded-full bg-ink" style={{ width: `${progress || 0}%` }} />
            <input
              type="range"
              value={progress || 0}
              onChange={handleSeek}
              className="absolute inset-0 w-full opacity-0 cursor-pointer"
              aria-label={i18nInstance.t('ui.ab.position')}
            />
          </div>
          <div className="flex justify-between text-[10px] text-muted font-mono tabular-nums">
            <span>{formatTime(currentTime)}</span>
            <span>{formatTime(duration)}</span>
          </div>
        </div>

        <div className="flex items-center justify-between">
          <button onClick={cycleRate} className="w-12 h-10 flex items-center justify-center font-mono text-[11px] text-ink bg-ink/[0.05] hover:bg-ink/[0.09] rounded-full transition-colors">
            {playbackRate}x
          </button>

          <div className="flex items-center gap-3">
            <button onClick={() => skip(-15)} className="w-11 h-11 rounded-full text-ink hover:bg-ink/[0.06] flex items-center justify-center transition-colors" title="-15 s">
              <AnimatedIcon motion="spin"><RotateCcw size={18} strokeWidth={1.75} /></AnimatedIcon>
            </button>

            <button
              onClick={togglePlay}
              disabled={isLoading}
              className="w-14 h-14 bg-primary text-slate-900 rounded-full flex items-center justify-center hover:scale-105 active:scale-95 transition-transform"
            >
              {isLoading ? <Loader2 className="animate-spin" size={22} /> : (isPlaying ? <Pause size={22} fill="currentColor" /> : <Play size={22} fill="currentColor" className="ml-1" />)}
            </button>

            <button onClick={() => skip(30)} className="w-11 h-11 rounded-full text-ink hover:bg-ink/[0.06] flex items-center justify-center transition-colors" title="+30 s">
              <AnimatedIcon motion="spin"><RotateCw size={18} strokeWidth={1.75} /></AnimatedIcon>
            </button>
          </div>

          <div className="relative" ref={containerRef}>
            <button
              onClick={() => setShowVolume(!showVolume)}
              className={`w-12 h-10 rounded-full flex items-center justify-center transition-colors ${showVolume ? 'bg-ink text-surface' : 'text-ink bg-ink/[0.05] hover:bg-ink/[0.09]'}`}
              title={t('audiobooks.adjust_volume', 'Ajustar Volume')}
            >
              <AnimatedIcon motion="wobble"><Volume2 size={17} strokeWidth={1.75} /></AnimatedIcon>
            </button>

            {showVolume && (
              <div className="absolute bottom-full right-0 mb-3 bg-surface p-4 rounded-[22px] shadow-[0_24px_60px_-24px_rgba(0,0,0,0.35)] border border-black/[0.06] dark:border-white/[0.06] flex flex-col items-center gap-3 animate-in slide-in-from-bottom-2 origin-bottom">
                <span className="text-[10px] font-mono text-ink tabular-nums">{Math.round(volume * 100)}%</span>
                <div className="h-28 w-6 flex items-center justify-center">
                  <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.01"
                    value={volume}
                    onChange={handleVolumeChange}
                    className="w-24 -rotate-90 appearance-none bg-ink/[0.08] h-1.5 rounded-full accent-[rgb(var(--ink))] cursor-pointer"
                  />
                </div>
              </div>
            )}
          </div>
        </div>

      </div>
    </motion.div>
  );
};

export default AudiobookPlayer;
